import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { AppShellProvider, type OpenCreateProjectOptions } from '../lib/app-shell-context.js'
import { sidebarUploadScopeProps } from '../lib/file-drag.js'
import { UploadManagerProvider } from '../lib/upload-manager.js'
import { AdminSidebar } from './AdminSidebar.js'
import { CreateProjectDialog } from './CreateProjectDialog.js'
import { EphemeralBanner } from './EphemeralBanner.js'
import { PageDropOverlay } from './PageDropOverlay.js'
import { ProjectsDndShell } from './ProjectsDndShell.js'
import { TenantSidebar } from './TenantSidebar.js'
import { UploadPanel } from './UploadPanel.js'

type Props = {
  variant: 'tenant' | 'admin'
}

export function AppShell({ variant }: Props) {
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createFolderId, setCreateFolderId] = useState<string | null>(null)

  // ルート遷移時にモバイル drawer を閉じる
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname の変化をトリガーにする
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const closeMobileSidebar = () => setMobileOpen(false)
  const openCreateProject = (opts?: OpenCreateProjectOptions) => {
    setCreateFolderId(opts?.folderId ?? null)
    setCreateOpen(true)
  }

  const shell = (
    <>
      {mobileOpen && (
        <button
          type="button"
          aria-label="サイドバーを閉じる"
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={closeMobileSidebar}
        />
      )}

      <aside
        {...sidebarUploadScopeProps}
        className={`fixed md:static inset-y-0 left-0 z-50 flex w-[240px] shrink-0 flex-col border-r border-border bg-card transition-transform duration-200 md:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        {variant === 'tenant' ? (
          <TenantSidebar onNavigate={closeMobileSidebar} />
        ) : (
          <AdminSidebar onNavigate={closeMobileSidebar} />
        )}
      </aside>

      <div className="relative flex flex-1 flex-col min-w-0">
        <div className="md:hidden shrink-0 flex items-center gap-3 border-b border-border bg-card/80 backdrop-blur px-4 h-12">
          <button
            type="button"
            aria-label="メニューを開く"
            onClick={() => setMobileOpen(true)}
            className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-border hover:bg-accent transition-colors"
          >
            <span aria-hidden className="text-lg leading-none">
              ☰
            </span>
          </button>
          <span className="text-sm font-bold tracking-tight">Terafile Bin</span>
        </div>

        {variant === 'tenant' && <EphemeralBanner />}
        <main data-app-scroll className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
        {variant === 'tenant' && <PageDropOverlay />}
      </div>

      {variant === 'tenant' && <UploadPanel />}

      {variant === 'tenant' && (
        <CreateProjectDialog
          open={createOpen}
          defaultFolderId={createFolderId}
          onClose={() => {
            setCreateOpen(false)
            setCreateFolderId(null)
          }}
        />
      )}
    </>
  )

  return (
    <AppShellProvider value={{ openCreateProject, closeMobileSidebar }}>
      <div className="flex h-screen overflow-hidden bg-background">
        {variant === 'tenant' ? (
          <UploadManagerProvider>
            <ProjectsDndShell>{shell}</ProjectsDndShell>
          </UploadManagerProvider>
        ) : (
          shell
        )}
      </div>
    </AppShellProvider>
  )
}
